import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { 
  Calendar as CalendarIcon, 
  Video, 
  Clock, 
  MapPin, 
  User, 
  ChevronRight, 
  PlusCircle, 
  Activity, 
  Bell, 
  Loader2,
  CalendarCheck
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import VideoCallModal from './VideoCallModal';
import { io } from 'socket.io-client';

const socket = io(`${window.location.protocol}//${window.location.hostname}`);

const PatientAppointments = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showCall, setShowCall] = useState(false);

    const userName = localStorage.getItem('userName') || 'Patient';
    const patientId = localStorage.getItem('patientId');
    const userRole = localStorage.getItem('userRole');
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchMyAppointments();
    }, []);

    const fetchMyAppointments = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${window.location.protocol}//${window.location.hostname}/api/appointments`, { headers });
            const data = Array.isArray(response.data) ? response.data : [];
            
            // If logged in as patient, filter their specific appointments
            // If admin, show all for this view (or better, show the staff the full context)
            const filtered = userRole === 'patient' 
                ? data.filter(a => String(a.patientId) === String(patientId))
                : data;
            
            setAppointments(filtered);
        } catch (error) {
            console.error('Error fetching appointments:', error);
            toast.error('Failed to load your schedule');
        } finally {
            setLoading(false);
        }
    };

    const events = useMemo(() => appointments.map(app => ({
        id: String(app.id),
        title: `${app.isVirtual ? '📹' : '🏥'} ${app.AppointmentType?.typeName || 'Consultation'}`,
        start: app.startTime,
        end: app.endTime,
        backgroundColor: app.isVirtual ? '#8b5cf6' : '#4f46e5',
        borderColor: 'transparent',
        extendedProps: { 
            ...app,
            doctorName: app.Doctor?.username || 'Attending Physician',
            location: app.isVirtual ? 'Telemedicine Link' : (app.location || 'Main Clinic')
        }
    })), [appointments]);

    const nextAppointment = useMemo(() => {
        if (appointments.length === 0) return null;
        const now = new Date();
        return appointments
            .filter(a => new Date(a.startTime) > now)
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))[0];
    }, [appointments]);

    const startCall = (appt) => {
        socket.emit('call-user', {
            toUserId: appt.doctorId || 'staff',
            fromUserName: userName,
            appointmentId: appt.id,
            patientId: appt.patientId,
            doctorId: appt.doctorId
        });
        setSelectedAppointment(appt);
        setShowCall(true);
    };

    const handleEventClick = (info) => {
        const { extendedProps, title } = info.event;
        toast((t) => (
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 border-b pb-2 mb-1">
                    <CalendarCheck className="w-4 h-4 text-indigo-600" />
                    <span className="font-black text-xs uppercase tracking-widest">{title}</span>
                </div>
                <div className="text-xs font-bold text-slate-600">
                    <p className="flex items-center gap-2"><User className="w-3 h-3"/> Dr. {extendedProps.doctorName}</p>
                    <p className="flex items-center gap-2 mt-1"><Clock className="w-3 h-3"/> {new Date(extendedProps.startTime).toLocaleString([], { hour: '2-digit', minute: '2-digit', weekday: 'short', month: 'short', day: 'numeric' })}</p>
                    <p className="flex items-center gap-2 mt-1 text-indigo-600"><MapPin className="w-3 h-3"/> {extendedProps.location}</p>
                </div>
            </div>
        ), { duration: 4000, position: 'bottom-right' });
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
        </div>
    );

    return (
        <div className="p-8 bg-slate-50 min-h-screen">
            <div className="max-w-[1600px] mx-auto">
                <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <h1 className="text-[36px] font-black text-slate-900 tracking-tight flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white shadow-xl shadow-indigo-100 flex items-center justify-center">
                                <CalendarIcon className="w-8 h-8 text-indigo-600" />
                            </div>
                            My Care Schedule
                        </h1>
                        <p className="text-slate-500 font-bold uppercase tracking-[0.15em] text-[10px] mt-4 ml-1 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Live Patient Engagement Portal
                        </p>
                    </div>
                    <div className="flex gap-4">
                        <Link to="/dashboard/patient-portal/my-appointments/book" className="flex items-center gap-3 px-8 py-4 bg-indigo-600 hover:bg-black text-white rounded-3xl font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-indigo-200 transition-all active:scale-95">
                            <PlusCircle className="w-4 h-4" />
                            Schedule Visit
                        </Link>
                    </div>
                </header>

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
                    <div className="xl:col-span-9">
                        <div className="bg-white p-8 rounded-[48px] border border-slate-200 shadow-2xl shadow-indigo-100/50 relative overflow-hidden">
                            <div className="absolute top-0 left-12 right-12 h-1 bg-gradient-to-r from-transparent via-indigo-200 to-transparent opacity-50" />
                            
                            <div className="calendar-premium-wrapper">
                                <FullCalendar
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                    initialView="timeGridWeek"
                                    headerToolbar={{
                                        left: 'prev,next today',
                                        center: 'title',
                                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                                    }}
                                    events={events}
                                    height="auto"
                                    eventClick={handleEventClick}
                                    slotMinTime="08:00:00"
                                    slotMaxTime="20:00:00"
                                    allDaySlot={false}
                                    expandRows={true}
                                    stickyHeaderDates={true}
                                    dayMaxEvents={true}
                                    eventTimeFormat={{
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        meridiem: 'short'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="xl:col-span-3 space-y-8">
                        {/* Dynamic Next Appointment Card */}
                        <div className={`rounded-[40px] p-8 text-white shadow-2xl transition-all relative overflow-hidden group ${nextAppointment ? (nextAppointment.isVirtual ? 'bg-purple-600 shadow-purple-200' : 'bg-indigo-600 shadow-indigo-200') : 'bg-slate-800'}`}>
                            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                                <Activity className="w-24 h-24" />
                            </div>
                            
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] mb-6 flex items-center gap-2 opacity-80">
                                <Clock className="w-3.5 h-3.5" />
                                Upcoming Insight
                            </h3>
                            
                            {nextAppointment ? (
                                <div className="space-y-6 relative z-10">
                                    <div>
                                        <p className="text-2xl font-black">{nextAppointment.AppointmentType?.typeName || 'Medical Visit'}</p>
                                        <p className="text-sm font-bold opacity-80 mt-1">
                                            {new Date(nextAppointment.startTime).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                                        </p>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-2xl">
                                            <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                                                <User className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black uppercase tracking-widest opacity-60">Practitioner</p>
                                                <p className="text-xs font-bold">Dr. {nextAppointment.Doctor?.username || 'Specialist'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-2xl">
                                            <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                                                <Clock className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black uppercase tracking-widest opacity-60">Time Slot</p>
                                                <p className="text-xs font-bold">
                                                    {new Date(nextAppointment.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <button 
                                        onClick={() => nextAppointment.isVirtual ? startCall(nextAppointment) : toast.success('Please visit the clinic for your appointment.')}
                                        className="w-full py-4 bg-white text-slate-900 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-xl active:scale-95"
                                    >
                                        {nextAppointment.isVirtual ? 'Start Video Call' : 'Check In Access'}
                                    </button>
                                </div>
                            ) : (
                                <div className="py-10 text-center">
                                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-20" />
                                    <p className="text-sm font-bold opacity-60 italic">Your calendar is currently clear.</p>
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-[40px] p-8 border border-slate-200 shadow-sm">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6">Patient Services</h3>
                            <div className="space-y-4">
                                <QuickAction 
                                    icon={Video} 
                                    label="Virtual Queue" 
                                    color="text-purple-600" 
                                    bgColor="bg-purple-50"
                                />
                                <QuickAction 
                                    icon={Bell} 
                                    label="Reminders" 
                                    color="text-amber-600" 
                                    bgColor="bg-amber-50"
                                />
                                <QuickAction 
                                    icon={Activity} 
                                    label="Health Stats" 
                                    color="text-emerald-600" 
                                    bgColor="bg-emerald-50"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <VideoCallModal 
                isOpen={showCall} 
                onClose={() => setShowCall(false)} 
                appointment={selectedAppointment} 
            />
            
            <style dangerouslySetInnerHTML={{ __html: `
                .calendar-premium-wrapper .fc-toolbar-title {
                    font-size: 1.25rem !important;
                    font-weight: 800 !important;
                    color: #1e293b !important;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .calendar-premium-wrapper .fc-button {
                    background: #f8fafc !important;
                    border: 1px solid #e2e8f0 !important;
                    color: #64748b !important;
                    font-size: 0.75rem !important;
                    font-weight: 700 !important;
                    text-transform: uppercase;
                    padding: 0.6rem 1rem !important;
                    border-radius: 0.75rem !important;
                    transition: all 0.2s !important;
                }
                .calendar-premium-wrapper .fc-button-active {
                    background: #4f46e5 !important;
                    color: white !important;
                    border-color: #4f46e5 !important;
                }
                .calendar-premium-wrapper .fc-event {
                    border-radius: 8px !important;
                    padding: 4px 8px !important;
                    font-size: 0.7rem !important;
                    font-weight: 700 !important;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
                }
                .calendar-premium-wrapper .fc-timegrid-slot {
                    height: 50px !important;
                }
                .calendar-premium-wrapper .fc-col-header-cell {
                    background: #f8fafc !important;
                    padding: 15px 0 !important;
                    border: none !important;
                }
                .calendar-premium-wrapper .fc-col-header-cell-cushion {
                    font-size: 0.75rem !important;
                    font-weight: 800 !important;
                    color: #64748b !important;
                    text-transform: uppercase;
                }
                .no-scrollbar::-webkit-scrollbar { display: none; }
            `}} />
        </div>
    );
};

const QuickAction = ({ icon: Icon, label, color, bgColor }) => (
    <button className={`w-full flex items-center justify-between p-4 rounded-3xl ${bgColor} group transition-all hover:scale-[1.02] active:scale-95`}>
        <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-lg shadow-indigo-500/5 ${color}`}>
                <Icon className="w-5 h-5" />
            </div>
            <span className="text-[12px] font-black text-slate-700 uppercase tracking-widest">{label}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:translate-x-1 transition-transform" />
    </button>
);

export default PatientAppointments;
